import {
  OPPORTUNITY_OPEN_STAGE_VALUES,
  opportunitiesResponseSchema,
  opportunityResponseSchema,
  type LoseOpportunityBody,
  type OpportunitiesQuery,
  type OpportunitiesResponse,
  type OpportunityView,
  type SellOpportunityBody,
} from "@gioia365/contracts";
import { infiniteQueryOptions, queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiRequest } from "../api/http";
import { appointmentKeys, appointmentsQuery } from "../appointments/appointments-queries";
import { leadKeys } from "../leads/lead-queries";
import type { Access } from "../session/access";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";
import { refreshSmartListMenu } from "../smart-lists/menu-refresh";
import { opportunityKeys } from "./opportunity-keys";

// Le letture e le scritture delle Opportunità (piano M6, R8, Task 25; contratti del Task 24): il riepilogo della pagina (il conto per
// fase, il valore delle vendute e le campagne: una richiesta con una riga sola), quello del mese («Vendute a …», «Perse a …»), le righe a
// pagine (`offset`) di una fase per ogni colonna di «Per fase» e di «Quali» per la «Tabella», con «Di chi», campagna e ricerca
// dell'indirizzo (il Commerciale soltanto le sue, SEZ-1: `ownOpportunitiesOf`); le Opportunità di un Lead per il riquadro e la finestra
// della scheda (`leadId`, `useLeadOpportunity`); il dettaglio; «Venduto», «Persa» e «Riapri» con la versione letta. Dopo un cambio si rileggono le Opportunità della scheda (pagina, riquadro e dettaglio), gli appuntamenti del Lead (che
// dicono la fase aperta), la sua Timeline, gli elenchi dei Lead (il filtro «Opportunità») e il menu delle Smart List (le liste sulle
// Opportunità). Il riepilogo e le colonne della vista di partenza li legge in anticipo la voce della rail.

/** Le righe di una pagina, di una colonna di «Per fase» o della «Tabella» (il server ne accetta da 1 a 500, di partenza 200). */
export const OPPORTUNITIES_PAGE_SIZE = 50;

/** Che cosa chiedere al server, senza `offset` né `limit`: la lettura delle pagine li aggiunge. */
export type OpportunitiesRequest = Pick<OpportunitiesQuery, "stage" | "who" | "campaign" | "q"> & Partial<Pick<OpportunitiesQuery, "period" | "from" | "to">>;

/**
 * SEZ-1 nella pagina: il Commerciale (un ruolo di livello 1; nella «Guarda come…» il livello della persona osservata, come la partenza
 * di «Di chi» dei Lead, LEAD-18) vede soltanto le sue Opportunità e non sceglie colleghi. Per lui la sua appartenenza, che ogni lettura
 * della pagina porta come `who` (il conto in cima e il mese compresi); per Super user, Admin, Super admin e la piattaforma `null`: «Di
 * chi», da «Tutto il team». Il riquadro della scheda non la usa: mostra anche l'Opportunità di un collega, in sola lettura.
 */
export function ownOpportunitiesOf(access: Access): string | null {
  const observed = access.context.commercialView;
  if (observed) return observed.role.level === 1 ? observed.membershipId : null;
  if (access.isPlatform) return null;
  return access.context.role?.level === 1 ? access.context.membershipId : null;
}

/** I filtri della vista di partenza dell'indirizzo `/opportunita` senza parametri: tutto il perimetro senza ricerca, o le sue (SEZ-1). */
export function defaultOpportunitiesFilters(access: Access): OpportunitiesRequest {
  const own = ownOpportunitiesOf(access);
  return own === null ? {} : { who: own };
}

/** Le colonne di «Per fase» della vista di partenza («Quali»: le aperte). */
const DEFAULT_COLUMNS = OPPORTUNITY_OPEN_STAGE_VALUES;

function listPath(query: OpportunitiesRequest & { leadId?: string }, offset: number, limit: number): string {
  const params = new URLSearchParams();
  if (query.stage !== undefined) params.set("stage", query.stage);
  if (query.who !== undefined) params.set("who", query.who);
  if (query.campaign !== undefined && query.campaign !== "") params.set("campaign", query.campaign);
  if (query.q !== undefined && query.q !== "") params.set("q", query.q);
  if (query.period !== undefined) params.set("period", query.period);
  if (query.from !== undefined) params.set("from", query.from);
  if (query.to !== undefined) params.set("to", query.to);
  if (query.leadId !== undefined) params.set("leadId", query.leadId);
  params.set("offset", String(offset));
  params.set("limit", String(limit));
  return `/api/opportunities?${params.toString()}`;
}

/**
 * Il riepilogo con i filtri dati (`limit=1`): il conto per fase, il valore delle vendute e le campagne, che non dipendono dalla fase.
 * Serve al conto in cima, a «Quali» e, con il periodo del mese, a «Vendute a …» e «Perse a …».
 */
export function opportunitiesSummaryQuery(tab: TabContext, query: OpportunitiesRequest, view: string | null) {
  return queryOptions({
    queryKey: opportunityKeys.summary(tabKey(tab), view, query),
    queryFn: ({ signal }) => apiRequest("GET", listPath(query, 0, 1), { schema: opportunitiesResponseSchema, tab, signal }),
    staleTime: 30_000,
  });
}

/** Le pagine delle Opportunità: la successiva finché le righe lette sono meno del totale. */
export function opportunitiesPagesQuery(tab: TabContext, query: OpportunitiesRequest, view: string | null) {
  return infiniteQueryOptions({
    queryKey: opportunityKeys.list(tabKey(tab), view, query),
    queryFn: ({ signal, pageParam }) => apiRequest("GET", listPath(query, pageParam, OPPORTUNITIES_PAGE_SIZE), { schema: opportunitiesResponseSchema, tab, signal }),
    initialPageParam: 0,
    getNextPageParam: (last: OpportunitiesResponse) => (last.rows.length > 0 && last.offset + last.rows.length < last.total ? last.offset + last.rows.length : undefined),
    // Tornare alla pagina entro mezzo minuto la mostra subito; un cambio dalla scheda la fa rileggere da sé.
    staleTime: 30_000,
  });
}

/**
 * L'Opportunità di adesso di un Lead, per il riquadro della scheda: quella aperta (al più una, SEZ-9) oppure l'ultima chiusa, come
 * la colonna e il filtro «Opportunità» dell'elenco; `null` se il Lead non ne ha nel perimetro di chi guarda.
 */
export function currentOpportunityOf(rows: readonly OpportunityView[]): OpportunityView | null {
  const open = rows.find((row) => row.stage !== "sold" && row.stage !== "lost");
  if (open) return open;
  const closed = rows.filter((row) => row.closedAt !== null).sort((left, right) => Date.parse(right.closedAt ?? "") - Date.parse(left.closedAt ?? ""));
  return closed[0] ?? rows[0] ?? null;
}

/** Le Opportunità del Lead (`GET /api/opportunities?leadId=…`): ne bastano poche, un Lead ne ha una per ogni disco dopo una chiusura. */
export function leadOpportunityQuery(tab: TabContext, leadId: string, view: string | null) {
  return queryOptions({
    queryKey: opportunityKeys.lead(tabKey(tab), view, leadId),
    queryFn: async ({ signal }) => {
      const page = await apiRequest("GET", listPath({ leadId }, 0, 50), { schema: opportunitiesResponseSchema, tab, signal });
      return currentOpportunityOf(page.rows);
    },
    staleTime: 30_000,
  });
}

/** L'Opportunità di adesso di un Lead per il riquadro e la finestra della scheda, o perché non c'è (`useLeadOpportunity`). */
export type LeadOpportunity =
  | { status: "loading" }
  | { status: "error"; error: unknown; retry: () => void }
  /** Il Lead ha un'Opportunità aperta fuori dal perimetro di chi guarda (la segue un collega e chi guarda vede le sue). */
  | { status: "notVisible" }
  /** `opportunity` è l'aperta o l'ultima chiusa, `null` se non ce n'è; `demoSeen` dice una demo fra i suoi appuntamenti. */
  | { status: "ready"; opportunity: OpportunityView | null; demoSeen: boolean };

/**
 * L'Opportunità di adesso del Lead, per il riquadro e la finestra «Opportunità di …» della scheda: la lettura del Lead nel perimetro
 * delle Opportunità e gli appuntamenti del Lead (R7, la stessa lettura di «Disco e demo»: nessuna richiesta in più), che dicono
 * l'Opportunità aperta qualunque sia quel perimetro. Se gli appuntamenti ne dicono una aperta che la lettura non porta, è fuori dal
 * perimetro di chi guarda; finché quella lettura è in corso (dopo la prima disco prenotata gli appuntamenti possono arrivare prima) si
 * aspetta, senza dire il perché. Non si aspetta la rilettura degli appuntamenti, che si rileggono da soli ogni minuto: una vera
 * Opportunità di un collega resterebbe ogni minuto per un attimo in caricamento.
 */
export function useLeadOpportunity(tab: TabContext, leadId: string, view: string | null, enabled: boolean): LeadOpportunity {
  const query = useQuery({ ...leadOpportunityQuery(tab, leadId, view), enabled });
  const appointments = useQuery({ ...appointmentsQuery(tab, leadId, view), enabled });
  if (query.isPending || (appointments.isPending && !appointments.isError)) return { status: "loading" };
  if (query.isError) return { status: "error", error: query.error, retry: () => void query.refetch() };
  const openId = appointments.data?.opportunity?.id ?? null;
  if (openId !== null && query.data?.id !== openId) return query.isFetching ? { status: "loading" } : { status: "notVisible" };
  const opportunity = query.data ?? null;
  const demoSeen =
    opportunity !== null &&
    (opportunity.appointment?.kind === "demo" || (appointments.data?.appointments ?? []).some((appointment) => appointment.opportunityId === opportunity.id && appointment.kind === "demo"));
  return { status: "ready", opportunity, demoSeen };
}

/** Il dettaglio di un'Opportunità con i suoi appuntamenti (`GET /api/opportunities/:id`), per «Com'è andata». */
export function opportunityDetailQuery(tab: TabContext, opportunityId: string, view: string | null) {
  return queryOptions({
    queryKey: opportunityKeys.detail(tabKey(tab), view, opportunityId),
    queryFn: ({ signal }) => apiRequest("GET", `/api/opportunities/${encodeURIComponent(opportunityId)}`, { schema: opportunityResponseSchema, tab, signal }),
    staleTime: 30_000,
  });
}

/**
 * Legge in anticipo la pagina di partenza (velocità: criterio di accettazione), per la voce della rail: il riepilogo e la prima pagina
 * delle quattro colonne aperte, con le chiavi della pagina aperta senza parametri (per il Commerciale le sue, SEZ-1), nella «Guarda
 * come…» della scheda se c'è. Non rilegge ciò che è ancora fresco. Il riepilogo del mese dipende dal giorno e lo legge la pagina.
 */
export async function prefetchOpportunities(queryClient: QueryClient, tab: TabContext, access: Access): Promise<void> {
  const view = commercialViewOf(tab);
  const filters = defaultOpportunitiesFilters(access);
  await Promise.all([
    queryClient.prefetchQuery(opportunitiesSummaryQuery(tab, filters, view)),
    ...DEFAULT_COLUMNS.map((stage) => queryClient.prefetchInfiniteQuery(opportunitiesPagesQuery(tab, { ...filters, stage }, view))),
  ]);
}

/** Dopo un cambio della fase (da qui o da un appuntamento): le Opportunità della scheda si rileggono, in ogni vista. */
export function refreshOpportunities(queryClient: QueryClient, tab: TabContext): void {
  void queryClient.invalidateQueries({ queryKey: opportunityKeys.all(tabKey(tab)) });
}

export type OpportunityCommand =
  | { action: "sold"; body: SellOpportunityBody }
  | { action: "lost"; body: LoseOpportunityBody }
  | { action: "reopen"; body: { version: number } };

/** «Venduto», «Persa» e «Riapri» dalla finestra «Opportunità di …» della scheda del Lead `leadId`, con la versione letta. */
export function useChangeOpportunity(tab: TabContext, leadId: string, view: string | null) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: ({ opportunityId, command }: { opportunityId: string; command: OpportunityCommand }) =>
      apiRequest("POST", `/api/opportunities/${encodeURIComponent(opportunityId)}/${command.action}`, { schema: opportunityResponseSchema, body: command.body, tab }),
    onSettled: () => {
      refreshOpportunities(queryClient, tab);
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.lead(tabPart, leadId, view) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, leadId, view) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists(tabPart) });
      refreshSmartListMenu(queryClient, tab);
    },
  });
}
